<template>
	<div id="app">
		<router-view :key="$route.fullPath" />
	</div>
</template>

<script setup>
import { onBeforeMount, onBeforeUnmount, onMounted, ref, watch,computed } from "vue"
import { changeLanguage } from "@/utils/i18n.js"
import { useRoute, useRouter } from "vue-router"
import initRem from "./utils/rem"
import {
	getAndroidInitParams,
	isAndroidEnv,
	getRFDeviceStatus,
} from "./utils/android"
import { useHrvStore } from "@/stores/hrvStore.js"
import { isEmptyObject } from "@/utils/hrv-utils"
import { ElMessage } from "element-plus"
import { waitForNewBuildReady } from "@/utils/update-ready.js"
import { useI18n } from "vue-i18n"
import { HRV_QUESTIONNAIRE_POSITION } from "@/constants/data"
initRem()
const route = useRoute()
const router = useRouter()

const bodyBackgroundColor = ref("#f3f4f5")
const hrvStore = useHrvStore()
const { t } = useI18n()

const questionnaireInfo = computed(() => hrvStore.getQuestionnaireInfo)
function setupAndroidCallbacks() {
	window.onGazeTrackingStopped = () => {
		console.log("onGazeTrackingStopped")
	}
	// rf报告回调
	window.onEcgReportComplete = (result) => {
		console.log("onEcgReportComplete", result)
		let rfReportData = null
		if (
			result.success &&
			!isEmptyObject(result?.hrvFeature) &&
			!isEmptyObject(result.rri) &&
			!isEmptyObject(result.hrvPsd)
		) {
			rfReportData = {
				rfMetrics: {
					HRV_HF: result?.hrvFeature?.hf || 0.0,
					HRV_LF: result?.hrvFeature?.lf || 0.0,
					HRV_TP: result?.hrvFeature?.tp || 0.0,
					HRV_VLF: result?.hrvFeature?.vlf || 0.0,
					HRV_LFHF: result?.hrvFeature?.lfHfRatio || 0.0,
					HRV_SDNN: result?.hrvFeature?.sdnn || 0.0,
					HRV_SDSD: result?.hrvFeature?.sdsd || 0.0,
					HRV_RMSSD: result?.hrvFeature?.rmssd || 0.0,
					HRV_pNN50: result?.hrvFeature?.pnn50 || 0.0,
					HRV_MeanNN: result?.hrvFeature?.meanNN || 0.0,
				},
				rriValues: result?.rri || null,
				psdLabelsValues: result?.hrvPsd || null,
			}
		}
		hrvStore.setRFReportData(rfReportData)
    if (
			questionnaireInfo.value?.enable &&
			questionnaireInfo.value?.checkStage ===
				HRV_QUESTIONNAIRE_POSITION.AFTER_CHECK
		) {
      console.log('去做问卷,onEcgReportComplete')
      // router.push({
      //   name: "HrvSurvey",
      //   query: {
      //     ...route.query
      //   },
      // }) 
      return
		}
		hrvStore.sendDataToBackend(router, route)
	}
}

function initDeviceStatus() {
	const rfstatus = getRFDeviceStatus()
	if (rfstatus) {
		hrvStore.setRFDeviceStatus(rfstatus)
	}
	console.log("🚀 ~ rfstatus:", rfstatus)
}

onBeforeMount(() => {
	setupAndroidCallbacks()
	initLanguage()
	initDeviceStatus()
})
const initLanguage = () => {
	let lang
	if (isAndroidEnv()) {
		const initData = getAndroidInitParams()
		console.log("mount", initData)
		hrvStore.setAndroidInitData(initData)
		lang = initData?.language || navigator.language
	} else {
		const urlParams = new URLSearchParams(window.location.search)
		const urlLanguage = urlParams.get("language")
		if (urlLanguage) {
			lang = urlLanguage.includes("zh") ? "zh-CN" : "en-US"
		} else {
      lang = navigator.language
		}
    console.log("🚀 ~ initLanguage ~ lang:", lang)
	}
	return changeLanguage(lang)
}
// 移除重复的 onBeforeMount，避免重复注册与冗余日志
let pendingUpdate = false // ✅ 白名单拦截刷新标记
let removeHook = null // ✅ 记录路由离开监听
onMounted(() => {
	window.addEventListener("app-update-detected", async () => {
		let abortCtrl
		let waitingMessage = null
		try {
			abortCtrl = new AbortController()
			const currentVersion = import.meta.env.VITE_APP_VERSION || ""

			ElMessage.closeAll()
			// waitingMessage = ElMessage.info({
			// 	message: "检测到新版本，正在检查更新…",
			// 	duration: 0,
			// 	showClose: false,
			// })

			const { entry } = await waitForNewBuildReady({
				currentVersion,
				maxWaitMs: 240_000,
				intervalMs: 3_000,
				maxAttempts: 80,
				deployWaitMs: 90_000,
				onWaiting: (attempt, remaining, lastError) => {
					const remainingSec = Math.ceil(remaining / 1000)
					const shouldNotify =
						attempt <= 3 || attempt % 5 === 0 || remainingSec <= 30
					if (shouldNotify) {
						ElMessage.closeAll()
						// ElMessage.info({
						// 	message: `新版本发布中（剩余${remainingSec}s）…`,
						// 	duration: 0,
						// 	showClose: false,
						// })
					}
				},
				signal: abortCtrl.signal,
			})

			ElMessage.closeAll()

			/** ✅ ✅ ✅ 增加：刷新白名单判断 */
			const currentPath = router.currentRoute.value.path
			if (currentPath.includes("hrvtest")) {
				pendingUpdate = true

				// ElMessage.warning({
				// 	message:
				// 		"新版本已发布，检测到您正在HRV测试中，刷新将稍后自动继续",
				// 	duration: 5000,
				// 	showClose: true,
				// })

				// ✅ 监听路由变化：离开 hrvtest 才刷新
				if (!removeHook) {
					removeHook = router.afterEach((to) => {
						if (!to.path.includes("hrvtest") && pendingUpdate) {
							setTimeout(() => location.reload(), 600)
						}
					})
				}

				return // ✅ 不立即刷新，先退出流程
			}

			// ✅ 普通情况下：立即刷新
			// ElMessage.success({
			// 	message: "新版本已就绪，正在刷新…",
			// 	duration: 1500,
			// })
			setTimeout(() => location.reload(), 800)
		} catch (e) {
			ElMessage.closeAll()
			console.error("[Update] Failed:", e)
			const isTimeout =
				e.message?.includes("timeout") ||
				e.message?.includes("Max attempts")
			// ElMessage.warning({
			// 	message: isTimeout
			// 		? "新版本仍在发布，请稍后再试或手动刷新"
			// 		: "检查更新失败，请稍后手动刷新",
			// 	duration: 4000,
			// 	showClose: true,
			// })
		} finally {
			ElMessage.closeAll()
			abortCtrl?.abort()
		}
	})
})
onBeforeUnmount(() => {
	// ✅ 移除 router.afterEach hook，防止泄漏
	removeHook && removeHook()
})
watch(
	() => route.path,
	(newName) => {
		if (newName.includes("hrvinit")) {
			bodyBackgroundColor.value = "transparent"
		} else {
			bodyBackgroundColor.value = "#f3f4f5"
		}
	},
	{ immediate: true }
)

watch(
	bodyBackgroundColor,
	(newColor) => {
		document.documentElement.style.setProperty(
			"--body-background-color",
			newColor
		)
	},
	{ immediate: true }
)
</script>

<style>
#app {
	display: flex;
	flex-direction: row;
	min-height: 100%;
	min-width: 100%;
	border-radius: 3.13rem;
}

body {
	background-color: var(--body-background-color, transparent);
	color: black;
}

#app > .router-view {
	flex: 1;
}

@media print {
	* {
		-webkit-print-color-adjust: exact;
		/* 针对 WebKit 浏览器 */
		print-color-adjust: exact;
		/* 标准属性 */
	}

	#app {
		padding: 0;
		background-color: white;
	}

	body {
		background-color: white;
	}
}
</style>
